<script lang="ts" setup>
import { notify } from "@kyvg/vue3-notification";
import { Jsona } from "jsona";
import type IJsonApiPageMeta from "~/interfaces/IJsonApiPageMeta";
import type { IJsonApiResponse } from "~/interfaces/IJsonApiResponse";
import Customer, { type ICustomer } from "~/models/Customer";
import Product, { type IProduct } from "~/models/Product";
import type { IProductAnswer } from "~/models/ProductAnswer";
import type ProductAnswer from "~/models/ProductAnswer";
import {
  ProductQuestion,
  type IProductQuestion,
} from "~/models/ProductQuestion";
import auth from "~/services/AuthService";
import { useIntersectionObserver } from "@vueuse/core";

const jsona = new Jsona();
const props = defineProps<{ product: Product }>();

const questions = reactive<ProductQuestion[]>([]);
const user = auth.getUser();
const apiFetch = useNuxtApp().$apiFetch;
const formSubmitting = ref(false);
const formError = ref<string>();
const questionForm = reactive(new ProductQuestion());
const currentQuestion = ref<ProductQuestion>();
const initialized = ref(false);
const editMode = ref(false);
const isPageLoading = ref(false);
const pageError = ref<string>();
const votingAnswerMap = reactive<Record<string, boolean | undefined>>({});
const pageMeta = reactive({
  currentPage: 1,
  from: 0,
  lastPage: 1,
  perPage: 3,
  to: 0,
  total: 0,
} as IJsonApiPageMeta);
const hasMore = computed(() => pageMeta.currentPage < pageMeta.lastPage);

const commonAnswerFields = () => {
  return {
    "fields[product-answers]":
      "answer,useful_votes,useless_votes,user_useful_voted,user_useless_voted",
  } as Record<string, string>;
};

const commonQuestionQuery = () => {
  return {
    "fields[product-questions]":
      "question,is_approved,customer,created_at_text,answers",
    "fields[customers]": "name,last_name",
    ...commonAnswerFields(),
    include: "customer,answers",
  } as Record<string, string>;
};

const saveQuestion = async () => {
  if (!user || !user.id || formSubmitting.value) {
    return;
  }
  formSubmitting.value = true;
  try {
    questionForm.answers = undefined;
    if (!questionForm.id) {
      questionForm.id = undefined;
    }
    questionForm.customer = new Customer({ id: user.id } as ICustomer);
    questionForm.product = new Product({ id: props.product.id } as IProduct);
    const input = jsona.serialize({ stuff: questionForm });
    const query = new URLSearchParams({
      ...commonQuestionQuery(),
    } as Record<string, string>).toString();
    const editUrl = questionForm.id ? `/${questionForm.id}` : "";
    const url = `/shop-v1/product-questions${editUrl}?${query}`;
    const doc = await apiFetch<IJsonApiResponse>(url, {
      method: questionForm.id ? "PATCH" : "POST",
      body: JSON.stringify(input),
    });
    const data = jsona.deserialize(doc) as IProductQuestion;
    questionForm.fill(data);
    currentQuestion.value = new ProductQuestion(data);
    editMode.value = false;
    notify({
      type: "success",
      title: "Tu pregunta ha sido envíada con éxito.",
    });
  } catch (err) {
    formError.value = await parseError(err);
  } finally {
    formSubmitting.value = false;
  }
};

const doLike = async (answer: ProductAnswer, vote: boolean) => {
  if (!user || !user.id || votingAnswerMap[answer.id || ""] !== undefined) {
    return;
  }
  votingAnswerMap[answer.id || ""] = vote;
  try {
    const query = new URLSearchParams({
      ...commonAnswerFields(),
    } as Record<string, string>).toString();
    const url =
      `/shop-v1/product-answers/${answer.id}/-actions/vote/` +
      (vote ? "1" : "0") +
      `?${query}`;
    const doc = await apiFetch<IJsonApiResponse>(url, { method: "POST" });
    const data = jsona.deserialize(doc) as IProductAnswer;
    answer.fill(data);
  } catch (err) {
    formError.value = await parseError(err);
  } finally {
    votingAnswerMap[answer.id || ""] = undefined;
  }
};

const getCurrentQuestion = async () => {
  if (!user || !user.id) {
    return;
  }
  try {
    const query = new URLSearchParams({
      "filter[customer_id]": user.id,
      "filter[product_id]": props.product.id,
      ...commonQuestionQuery(),
    } as Record<string, string>).toString();
    const url = `/shop-v1/product-questions?${query}`;
    const doc = await apiFetch<IJsonApiResponse>(url);
    const data = jsona.deserialize(doc) as IProductQuestion[];
    if (Array.isArray(data) && data.length) {
      currentQuestion.value = new ProductQuestion(data[0]);
      questionForm.fill(currentQuestion.value);
    }
  } catch (err) {
    console.error(err);
    formError.value = await parseError(err);
  }
};

const paginate = async () => {
  const user = auth.getUser();
  if (!props.product.id || isPageLoading.value) {
    return;
  }
  isPageLoading.value = true;
  try {
    const path = `/shop-v1/product-questions`;
    const query = new URLSearchParams({
      "page[number]": `${pageMeta.currentPage}`,
      "page[size]": `${pageMeta.perPage}`,
      "filter[product_id]": `${props.product.id}`,
      ...(user?.id ? { "filter[-customer_id]": user.id } : undefined),
      sort: "-id",
      ...commonQuestionQuery(),
      // ...(user?.id ? { "filter[except_customer_id]": user.id } : undefined),
    } as Record<string, string>).toString();
    const queryStr = new URLSearchParams(query).toString();
    const doc = await apiFetch<IJsonApiResponse>(path + "?" + queryStr);
    const jsonapi = jsona.deserialize(doc);
    const instances = ProductQuestion.fromArray(jsonapi as IProductQuestion[]);
    const meta = doc.meta as { page: IJsonApiPageMeta } | undefined;
    questions.push(...instances);
    if (meta) {
      Object.assign(pageMeta, meta.page);
    }
  } catch (err) {
    pageError.value = await parseError(err);
  } finally {
    isPageLoading.value = false;
  }
};

const nextPage = async () => {
  if (hasMore.value) {
    pageMeta.currentPage++;
    await paginate();
  }
};

const initialize = async () => {
  await Promise.all([getCurrentQuestion(), paginate()]);
  initialized.value = true;
};

watch(
  () => editMode.value || currentQuestion.value,
  () => {
    if (currentQuestion.value) {
      if (editMode.value) {
        questions.splice(0, 1);
      } else {
        questions.unshift(currentQuestion.value);
      }
    }
  },
);

const questionSection = ref<HTMLElement>();
let sectionIntersected = false;
useIntersectionObserver(questionSection, ([observer]) => {
  if (!sectionIntersected && observer?.isIntersecting) {
    sectionIntersected = true;
    initialize();
  }
});
</script>

<template>
  <div id="product-question-section" ref="questionSection">
    <div
      v-if="initialized && !currentQuestion && !questions.length"
      class="alert alert-light"
      role="alert"
    >
      Todavía no hay preguntas para este producto, sé el primero en hacerlo.
    </div>
    <form
      v-if="initialized && (!currentQuestion || editMode)"
      method="POST"
      class="mb-3 card text-center"
      @submit.prevent="saveQuestion()"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center flex-wrap"
      >
        <strong>{{
          questionForm.id ? "Has una pregunta" : "Has una pregunta"
        }}</strong>
      </div>
      <div v-if="!user" class="alert alert-secondary m-3" role="alert">
        Inicia sesión para poder preguntar.
      </div>
      <div v-else class="card-body">
        <div class="mb-3">
          <label for="question-input" class="form-label"
            >Escribe tu pregunta</label
          >
          <input
            id="question-input"
            v-model="questionForm.question"
            type="text"
            class="form-control text-center"
            maxlength="80"
            required
          />
          <div id="passwordHelpBlock" class="form-text">
            {{ questionForm.question?.length || 0 }} de 120 carácteres
            disponibles
          </div>
        </div>
        <div class="d-flex gap-2 justify-content-center">
          <button
            type="submit"
            class="btn btn-outline-primary"
            :disabled="formSubmitting"
          >
            <i v-if="!formSubmitting" class="bi bi-send"></i>
            <span
              v-else
              class="spinner-border spinner-border-sm text-secondary me-1"
              role="status"
            ></span>
            {{ currentQuestion ? "Confirmar" : "Enviar pregunta" }}
          </button>
          <button
            v-if="questionForm.id"
            type="button"
            class="btn btn-outline-dark"
            :disabled="formSubmitting"
            @click="editMode = false"
          >
            <i class="bi bi-arrow-left-circle"></i>
            Cancelar
          </button>
        </div>
        <div
          v-if="formError"
          class="alert alert-danger alert-dismissible mt-2"
          role="alert"
        >
          <pre class="mb-0">{{ formError }}</pre>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="alert"
          ></button>
        </div>
      </div>
    </form>
    <p v-if="!initialized" class="placeholder-glow" aria-hidden="true">
      <span class="placeholder col-12 bg-light" style="height: 80px"></span>
    </p>
    <div
      v-for="question in questions"
      :key="question.id || undefined"
      class="card text-center mb-3"
      :class="{ shadow: currentQuestion?.id === question.id }"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center position-relative"
      >
        <div class="small">
          <i class="bi bi-person"></i>
          {{ question.customer?.name }}
        </div>
        <span
          v-if="currentQuestion?.id === question.id"
          class="position-absolute top-0 start-50 translate-middle badge rounded-pill bg-secondary"
          >Tu pregunta</span
        >
      </div>
      <div class="card-body mb-0">
        <h5 class="card-title mb-0 ms-auto">
          {{ question.question }}
          <i class="bi bi-question-circle float-end"></i>
        </h5>
        <button
          v-if="
            currentQuestion?.id === question.id &&
            !editMode &&
            !question.is_approved
          "
          type="button"
          class="btn btn-outline-dark btn-sm mt-3"
          @click="editMode = true"
        >
          <i class="bi bi-pencil me-1"></i> Editar pregunta
        </button>
        <ul
          v-if="question.answers?.length"
          class="list-group ist-group-flush mt-3"
        >
          <li
            v-for="answer in question.answers"
            :key="answer.id"
            class="list-group-item"
          >
            <div class="d-flex justify-content-between">
              <p>{{ answer.answer }}</p>
              <i class="bi bi-chat-left-quote"></i>
            </div>
            <div class="d-flex gap-1 justify-content-start">
              <div>
                <div class="input-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-success"
                    :disabled="
                      votingAnswerMap[answer.id || ''] !== undefined || !user
                    "
                    @click="doLike(answer, true)"
                  >
                    <i
                      v-if="votingAnswerMap[answer.id || ''] !== true"
                      class="bi"
                      :class="[
                        answer.user_useful_voted
                          ? 'bi-hand-thumbs-up-fill'
                          : 'bi-hand-thumbs-up',
                      ]"
                    ></i>
                    <span
                      v-else-if="votingAnswerMap[answer.id || ''] === true"
                      class="spinner-border spinner-border-sm text-secondary"
                      role="status"
                    ></span>
                  </button>
                  <span
                    class="input-group-text px-2 border-success text-secondary"
                    ><small>{{ answer.useful_votes }}</small></span
                  >
                </div>
              </div>
              <div>
                <div class="input-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="
                      votingAnswerMap[answer.id || ''] !== undefined || !user
                    "
                    @click="doLike(answer, false)"
                  >
                    <i
                      v-if="votingAnswerMap[answer.id || ''] !== false"
                      class="bi"
                      :class="[
                        answer.user_useless_voted
                          ? 'bi-hand-thumbs-down-fill'
                          : 'bi-hand-thumbs-down',
                      ]"
                    ></i>
                    <span
                      v-else-if="votingAnswerMap[answer.id || ''] === false"
                      class="spinner-border spinner-border-sm text-secondary"
                      role="status"
                    ></span>
                  </button>
                  <span
                    class="input-group-text px-2 border-success text-secondary"
                    ><small>{{ answer.useless_votes }}</small></span
                  >
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div v-else class="alert alert-light mb-0 mt-2" role="alert">
          Todavía no hay respuestas, revisa en otro momento.
        </div>
      </div>
      <div class="card-footer text-body-secondary text-start small">
        <i class="bi bi-clock"></i> {{ question.created_at_text }}
      </div>
    </div>
    <div v-if="pageError" class="alert alert-danger mb-3" role="alert">
      {{ pageError }}
    </div>
    <div
      v-if="(hasMore || (initialized && isPageLoading)) && questions.length"
      class="text-center"
    >
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        :disabled="isPageLoading"
        @click="nextPage()"
      >
        <i v-if="!isPageLoading" class="bi bi-arrow-down"></i>
        <span
          v-else
          class="spinner-border spinner-border-sm text-danger me-1"
          role="status"
        ></span>
        Cargar más preguntas
      </button>
    </div>
  </div>
</template>
